'use client';

import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useTranslation } from '@/hooks/useTranslation';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date()?.getFullYear();

  const footerLinks = {
    platform: [
      { label: t('manufacturers'), href: '/manufacturer-directory' },
      { label: t('products'), href: '/product-catalog' },
      { label: t('allCategories'), href: '/industry-categories' },
      { label: t('viewDocs'), href: '/docs' },
    ],
    resources: [
      { label: t('sourcingGuide'), href: '/sourcing-guide' },
      { label: t('qualityStandards'), href: '/quality-standards' },
      { label: t('verificationProcess'), href: '/verification-process' },
      { label: t('helpCenter'), href: '/help-center' },
    ],
    company: [
      { label: t('aboutUs'), href: '/about-us' },
      { label: t('contact'), href: '/contact' },
      { label: t('blog'), href: '/blog' },
    ],
    legal: [
      { label: t('privacyPolicy'), href: '/privacy-policy' },
      { label: t('termsOfService'), href: '/terms-of-service' },
      { label: t('cookiePolicy'), href: '/cookie-policy' },
      { label: t('gdprCompliance'), href: '/gdpr-compliance' },
    ],
  };

  return (
    <footer className="bg-card border-t border-border">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          <div className="lg:col-span-1">
            <Link href="/homepage" className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 rounded-md flex items-center justify-center">
                <img
                  src="/logo.png"
                  alt="EgypTex Gateway Logo"
                  className="w-10 h-10 object-contain"
                />
              </div>
              <span className="text-lg font-headline font-bold text-foreground">
                EgypTex Gateway
              </span>
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              {t('connectingManufacturers')}
            </p>
            <div className="flex gap-3">
              <a
                href="https://egyptexgateway.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-muted rounded-md flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors duration-200"
              >
                <Icon name="GlobeAltIcon" size={18} variant="outline" />
              </a>
              <Link
                href="/contact"
                className="w-9 h-9 bg-muted rounded-md flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors duration-200"
              >
                <Icon name="EnvelopeIcon" size={18} variant="outline" />
              </Link>
              <a
                href="https://wa.me/+201044868081"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-muted rounded-md flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors duration-200"
              >
                <Icon name="PhoneIcon" size={18} variant="outline" />
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-headline font-bold text-foreground mb-4">{t('platform')}</h3>
            <ul className="space-y-3">
              {footerLinks?.platform?.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link?.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-headline font-bold text-foreground mb-4">{t('resources')}</h3>
            <ul className="space-y-3">
              {footerLinks?.resources?.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link?.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-headline font-bold text-foreground mb-4">{t('company')}</h3>
            <ul className="space-y-3">
              {footerLinks?.company?.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link?.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-headline font-bold text-foreground mb-4">{t('legal')}</h3>
            <ul className="space-y-3">
              {footerLinks?.legal?.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link?.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © {currentYear} EgypTex Gateway. {t('allRightsReserved')}.
            </p>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Icon name="ShieldCheckIcon" size={16} variant="solid" className="text-success" />
                <span>{t('isoCertified')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="LockClosedIcon" size={16} variant="solid" className="text-primary" />
                <span>{t('gdprCompliant')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;